import { useEffect, useState } from "react";
import { CheckCircle, Zap, TrendingUp } from "lucide-react";

const ProcessingPage = ({ result, onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    "Parsing manifest data...",
    "Cleaning CSV fields...",
    "Calculating pricing rules...",
    "Generating Shopify handles...",
    "Finalizing export data..."
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => onComplete(), 2000); // Increased delay to show completion
          return 100;
        }
        return prev + 1; // Slower progress for better user experience
      });
      
      setCurrentStep(Math.floor((progress / 100) * steps.length));
    }, 80); // Slower interval for better animation

    return () => clearInterval(interval);
  }, [progress, onComplete]);

  const getCategoryBadgeClass = (category) => {
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('baby') || categoryLower.includes('care')) {
      return 'category-badge category-badge-baby';
    } else if (categoryLower.includes('apparel') || categoryLower.includes('clothing')) {
      return 'category-badge category-badge-apparel';
    }
    return 'category-badge category-badge-general';
  };

  const getPricingPercentage = (category) => {
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('baby') || categoryLower.includes('care')) {
      return '70%';
    } else if (categoryLower.includes('apparel') || categoryLower.includes('clothing')) {
      return '50%';
    }
    return '65%';
  };

  // Sample items for preview (first 3)
  const sampleItems = result.items.slice(0, 3);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 
          className="text-4xl sm:text-5xl font-black tracking-tight heading-font" 
          data-testid="processing-title"
        >
          Processing Your Data
        </h1>
        <p className="text-lg text-slate-600 mono-font" data-testid="processing-subtitle">
          {result.processed_items} items • {result.filename}
        </p>
      </div>

      {/* Progress Section */}
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-white border border-slate-200 rounded-sm p-8" data-testid="progress-section">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700 mono-font" data-testid="progress-percentage">
                {progress.toFixed(0)}% Complete
              </span>
              <Zap className="w-5 h-5 text-emerald-600" />
            </div>
            
            <div className="progress-dock" data-testid="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            
            <p className="text-sm text-slate-600 mono-font" data-testid="current-step">
              {currentStep < steps.length ? steps[currentStep] : "Processing complete!"}
            </p>
          </div>
        </div>

        {/* Before/After Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Before */}
          <div className="bg-slate-50 rounded-sm p-6 space-y-4" data-testid="before-section">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-slate-400 rounded-full"></div>
              <h3 className="font-bold heading-font">Raw Manifest Data</h3>
            </div>
            
            <div className="space-y-3 mono-font text-xs">
              {sampleItems.map((item, index) => (
                <div key={index} className="bg-white p-3 border border-slate-200 rounded" data-testid={`raw-item-${index}`}>
                  <div className="grid grid-cols-2 gap-2 text-slate-600">
                    <div>Desc: {item.description.substring(0, 20)}...</div>
                    <div>SKU: {item.item_number}</div>
                    <div>Retail: ${item.retail_price.toFixed(2)}</div>
                    <div>Cat: {item.category_description.substring(0, 15)}...</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* After */}
          <div className="bg-emerald-50 rounded-sm p-6 space-y-4" data-testid="after-section">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-emerald-600 rounded-full"></div>
              <h3 className="font-bold heading-font">Refined Shopify Data</h3>
            </div>
            
            <div className="space-y-3 mono-font text-xs">
              {sampleItems.map((item, index) => (
                <div key={index} className="bg-white p-3 border border-emerald-200 rounded" data-testid={`refined-item-${index}`}>
                  <div className="space-y-2">
                    <div className="font-medium text-emerald-900">{item.description}</div>
                    <div className="grid grid-cols-2 gap-2 text-slate-600">
                      <div>Handle: {item.shopify_handle}</div>
                      <div className={getCategoryBadgeClass(item.category_description)}>
                        {getPricingPercentage(item.category_description)}
                      </div>
                    </div>
                    <div className="flex space-x-4 text-emerald-700 font-medium">
                      <div>Sell: ${item.selling_price.toFixed(2)}</div>
                      <div>Profit: ${item.profit_amount.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-sm p-6 text-center" data-testid="total-items-stat">
            <div className="text-2xl font-black heading-font text-slate-900">
              {result.processed_items}
            </div>
            <div className="text-sm text-slate-600 mono-font">Items Processed</div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-sm p-6 text-center" data-testid="pricing-rules-stat">
            <div className="text-2xl font-black heading-font text-emerald-700">
              3
            </div>
            <div className="text-sm text-slate-600 mono-font">Pricing Rules Applied</div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-sm p-6 text-center" data-testid="avg-markup-stat">
            <div className="text-2xl font-black heading-font text-emerald-700 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 mr-1" />
              55%
            </div>
            <div className="text-sm text-slate-600 mono-font">Profit Margin</div>
          </div>
        </div>

        {progress >= 100 && (
          <div className="text-center py-4" data-testid="completion-indicator">
            <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
            <p className="text-lg font-medium text-emerald-700 mono-font">Processing Complete!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessingPage;